import {
  Button,
  collapseColumn,
  column,
  Css,
  defaultTestId,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  Icon,
  simpleHeader,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormStates } from "@homebound/form-state";
import { isAfter, isBefore, isEqual, isWithinInterval, startOfToday } from "date-fns";
import { useMemo } from "react";
import {
  PersonalDashboard_ScheduleFragment,
  PersonalDashboard_TaskFragment,
  TaskStatusesFragment,
} from "src/fragments/circularFragments.generated";
import { InputMaybe, SaveScheduleTaskInput, ScheduleStatus, TaskStatus, TaskType } from "src/generated/graphqlTypes";
import { useDashboardFilterContext } from "src/routes/personal-dashboard/DashboardFilterContext";
import { UpcomingDueIn } from "src/routes/personal-dashboard/enums";
import { TaskStatusSelect } from "src/routes/projects/schedule-v2/components/TaskStatusSelect";
import { useTaskBillContext } from "src/routes/projects/schedule-v2/contexts/TaskBillModalContext";
import { useSaveScheduleTasksMutation } from "src/routes/projects/schedule-v2/ProjectSchedulePageV2.generated";
import { assertNever } from "src/utils";
import { addDaysDateOnly, DateOnly, formatMonthDay, formatWithShortYear } from "src/utils/dates";
import { PersonalDashboardRefetchQueries } from "../../enums/consts";
import { TaskRowIcon, TaskTitleAndParent } from "./CellComponents";
import { useSaveUpcomingPlanTaskMutation } from "./UpcomingPage.generated";

type UpcomingTasksTableProps = {
  tasks: PersonalDashboard_TaskFragment[];
  taskStatuses: TaskStatusesFragment[];
  onTaskSelect: (taskId: string, tabSelected?: string, scrollIntoViewType?: string) => void;
};

type UpcomingTaskFormInput = Partial<Pick<SaveScheduleTaskInput, "id" | "status">> & { type: TaskType };

export function UpcomingTasksTable({ tasks, taskStatuses, onTaskSelect }: UpcomingTasksTableProps) {
  const {
    filter: { dueIn },
    selectedTaskId,
  } = useDashboardFilterContext();
  const [saveScheduleTasks] = useSaveScheduleTasksMutation();
  const [savePlanTask] = useSaveUpcomingPlanTaskMutation();
  const { openTaskBillModal: maybeOpenTaskBillModal } = useTaskBillContext();

  const { getFormState } = useFormStates<Partial<UpcomingTaskFormInput>, PersonalDashboard_TaskFragment>({
    config: formConfig,
    map: (task) => ({ id: task.id, status: task.status.code, type: task.type.code }),
    autoSave: async (os) => {
      const { id, status } = os.changedValue;
      const type = os.type.value;

      if (type === TaskType.PlanTask) {
        await savePlanTask({
          variables: { input: { id: os.value.id!, status: os.status.changedValue } },
          refetchQueries: PersonalDashboardRefetchQueries,
        });
      }

      if (status === TaskStatus.Complete) {
        maybeOpenTaskBillModal({
          taskId: os.value.id!,
          handleTaskSave: () =>
            saveScheduleTasks({
              variables: { input: { tasks: [{ status, id }] } },
              refetchQueries: PersonalDashboardRefetchQueries,
            }),
        });
      } else if (type === TaskType.ScheduleTask) {
        await saveScheduleTasks({
          variables: { input: { tasks: [{ status, id }] } },
          refetchQueries: PersonalDashboardRefetchQueries,
        });
      }

      os.commitChanges();
    },
    getId: (t) => t.id,
  });

  const { rows, columns } = useMemo(
    () => ({
      columns: createColumns(getFormState, taskStatuses, selectedTaskId, onTaskSelect),
      rows: createRows(tasks, dueIn),
    }),
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, dueIn, getFormState, taskStatuses, onTaskSelect],
  );

  return (
    <div css={Css.h100.$}>
      <GridTable
        stickyHeader
        as="virtual"
        rows={rows}
        columns={columns}
        style={{ allWhite: true, bordered: false, rowHover: false }}
        rowStyles={{ header: { cellCss: Css.xsSb.gray900.$ }, scheduleTask: { cellCss: Css.pxPx(12).$ } }}
      />
    </div>
  );
}

type HeaderRow = { kind: "header"; id: string; data: undefined };
type GroupingRow = { kind: "grouping"; data: { title: string; subTitle: string }; children: TaskRow[] };
type TaskRow = { kind: "scheduleTask"; id: string; data: PersonalDashboard_TaskFragment };
type Row = HeaderRow | GroupingRow | TaskRow;

function createColumns(
  getFormState: (input: PersonalDashboard_TaskFragment) => ObjectState<Partial<UpcomingTaskFormInput>>,
  taskStatuses: TaskStatusesFragment[],
  selectedTaskId: string | undefined,
  onTaskSelect: (taskId: string, tabSelected?: string, scrollIntoViewType?: string) => void,
): GridColumn<Row>[] {
  return [
    collapseColumn<Row>({ header: emptyCell }),
    column<Row>({
      header: emptyCell,
      grouping: ({ title, subTitle }) => ({
        content: () => {
          const testId = defaultTestId(title);
          return (
            <div data-testid={`dueInGrouping_${testId}`}>
              <span css={Css.mdSb.pr1.$}>{title}</span>
              <span css={Css.md.$}>({subTitle})</span>
            </div>
          );
        },
        colspan: 2,
      }),
      scheduleTask: (scheduleTask) => <TaskTitleAndParent task={scheduleTask} onTaskSelect={onTaskSelect} />,
      w: 4,
    }),
    column<Row>({
      w: "50px",
      header: emptyCell,
      grouping: emptyCell,
      scheduleTask: (task) => {
        if (task.checklistComplete === null) return emptyCell;
        return (
          <button onClick={() => onTaskSelect(task.id, "details", "checklistItems")}>
            <Icon
              icon={task.checklistComplete ? "checklistComplete" : "checklistNotComplete"}
              data-testid={task.checklistComplete ? "checklistComplete" : "checklistNotComplete"}
            />
          </button>
        );
      },
    }),
    column<Row>({
      header: emptyCell,
      grouping: emptyCell,
      scheduleTask: (scheduleTask) => <TaskRowIcon task={scheduleTask} />,
      w: "70px",
    }),
    column<Row>({
      header: "Due",
      grouping: emptyCell,
      scheduleTask: (scheduleTask) => <span css={Css.xs.$}>{formatWithShortYear(scheduleTask.interval.endDate)}</span>,
      w: 1,
    }),
    column<Row>({
      header: "Status",
      grouping: emptyCell,
      scheduleTask: (task) => {
        const os = getFormState(task);
        const { schedule, type } = task;
        const disabled =
          type.code === TaskType.ScheduleTask &&
          ((schedule as PersonalDashboard_ScheduleFragment).isLocked ||
            (schedule as PersonalDashboard_ScheduleFragment).status === ScheduleStatus.Draft);
        return (
          <TaskStatusSelect
            statusField={os.status}
            options={taskStatuses}
            canComplete={task.canComplete}
            canStart={task.canStart}
            disabled={disabled}
            hideLabel
            dataTestId="upcomingTasks"
          />
        );
      },
      w: "150px",
    }),
    column<Row>({
      header: emptyCell,
      grouping: emptyCell,
      scheduleTask: ({ id }) => (
        <Button
          disabled={selectedTaskId === id}
          onClick={() => onTaskSelect(id, "details", undefined)}
          label="View Task"
        />
      ),
      w: "120px",
    }),
  ];
}

function createRows(
  tasks: PersonalDashboard_TaskFragment[],
  dueInFilter: InputMaybe<UpcomingDueIn>,
): GridDataRow<Row>[] {
  const rowsTest = tasks.map((t) => ({ kind: "scheduleTask" as const, id: t.id, data: t }));

  const groups = [
    { id: UpcomingDueIn.STALE, title: "Stale Tasks", children: rowsTest },
    { id: UpcomingDueIn.TOMORROW, title: "Due Tomorrow", children: [] },
    { id: UpcomingDueIn.ONE_WEEK, title: "Due in 1 Week", children: [] },
    { id: UpcomingDueIn.THREE_WEEKS, title: "Due in 3 Weeks", children: [] },
    { id: UpcomingDueIn.ONE_MONTH, title: "Due in 1 Month", children: [] },
    { id: UpcomingDueIn.OVER_ONE_MONTH, title: "More than 1 Month", children: [] },
  ];

  const filteredGroups = !dueInFilter ? groups : groups.filter((group) => group.id === dueInFilter);

  const groupedRows = filteredGroups.map((group) => ({
    kind: "grouping" as const,
    id: group.id,
    data: { title: group.title, subTitle: getDateRangeString(group.id) },
    children: getChildRowsForGrouping(group.id, tasks),
  }));

  return [simpleHeader, ...groupedRows];
}

function getChildRowsForGrouping(group: UpcomingDueIn, tasks: PersonalDashboard_TaskFragment[]) {
  const { startDate, endDate } = getDateRangesForDueIn(group);

  const filteredTasks = tasks.filter((t) => {
    const taskEndDate = t.interval.endDate;

    if (!startDate && endDate) return isBefore(taskEndDate, endDate) || isEqual(taskEndDate, endDate);
    if (startDate && !endDate) return isAfter(taskEndDate, startDate) || isEqual(taskEndDate, startDate);

    return isWithinInterval(taskEndDate, { start: startDate, end: endDate });
  });

  return filteredTasks.map((t) => ({ kind: "scheduleTask" as const, id: `${group}-${t.id}`, data: t }));
}

function getDateRangeString(dueIn: UpcomingDueIn) {
  const { startDate, endDate } = getDateRangesForDueIn(dueIn);

  if (startDate && !endDate) return `${formatMonthDay(startDate)} +`;
  if (!startDate && endDate) return "Before Today";
  if (startDate === endDate) return formatMonthDay(startDate);

  return `${formatMonthDay(startDate)} - ${formatMonthDay(endDate)}`;
}
function getDateRangesForDueIn(dueIn: UpcomingDueIn) {
  const today = new DateOnly(startOfToday());

  switch (dueIn) {
    case UpcomingDueIn.STALE:
      return { startDate: undefined, endDate: addDaysDateOnly(today, -1) };

    case UpcomingDueIn.TOMORROW:
      const tomorrow = addDaysDateOnly(today, 1);
      return { startDate: tomorrow, endDate: tomorrow };

    case UpcomingDueIn.ONE_WEEK:
      return { startDate: today, endDate: addDaysDateOnly(today, 6) };

    case UpcomingDueIn.THREE_WEEKS:
      return { startDate: today, endDate: addDaysDateOnly(today, 20) };

    case UpcomingDueIn.ONE_MONTH:
      return { startDate: today, endDate: addDaysDateOnly(today, 28) };

    case UpcomingDueIn.OVER_ONE_MONTH:
      return { startDate: addDaysDateOnly(today, 29), endDate: undefined };

    default:
      return assertNever(dueIn);
  }
}

const formConfig: ObjectConfig<UpcomingTaskFormInput> = {
  id: { type: "value" },
  status: { type: "value" },
  type: { type: "value" },
};
